"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
  useMemo,
} from "react";
import { toast } from "sonner";
import { graphqlFetch } from "@/lib/graphql-client";
import { DELETE_APPOINTMENT } from "@/lib/apollo/queries/booking-edge";
import {
  BookingContextType,
  BookingService,
  BookingGuest,
  GroupedServices,
  BookingPackage,
  Salon,
  SalonStaff,
  Client,
  AppointmentPreview,
  PackageVoucher,
  ClientMilestone,
  VoucherService,
  Appointment,
  Service,
  ServiceVariant,
  Package,
  Bundle,
  BundleScheduleType,
  Promo,
  Business,
  BookingStep,
  BookingTabName,
  WorkflowForm,
  WorkflowFormAnswers,
  GiftCardCartItem,
  Product,
  BookingProduct,
  ProductOrderSummary,
  MAX_ADDITIONAL_GUESTS,
} from "@/types/booking";

const BookingContext = createContext<BookingContextType | undefined>(undefined);

/** Matches a cart line by service id and optional guest id. */
const matchesServiceGuest = (
  service: BookingService,
  serviceId: string,
  guestId?: string | null
) =>
  service.id === serviceId &&
  (service.guestId ?? null) === (guestId ?? null);

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error("useBooking must be used within BookingProvider");
  }
  return context;
}

interface BookingProviderProps {
  children: ReactNode;
  businessInfo: Business | null;
  salons: Salon[];
  services: Service[];
  popularServices?: Service[];
  packages: Package[];
  bundles: Bundle[];
  products?: Product[];
  faqs?: any[];
  reviews?: any[];
  promos?: Promo[];
  initialSelectedSalonId?: string | null;
  servicesLoading?: boolean;
}

export function BookingProvider({
  children,
  businessInfo: initialBusinessInfo,
  salons: initialSalons,
  services: initialServices,
  popularServices: initialPopularServices = [],
  packages: initialPackages,
  bundles: initialBundles,
  products: initialProducts = [],
  faqs = [],
  reviews = [],
  promos = [],
  initialSelectedSalonId = null,
  servicesLoading: externalServicesLoading = false,
}: BookingProviderProps) {
  // Core State
  const [step, setStep] = useState(BookingStep.INITIAL);
  const [businessInfo] = useState<Business | null>(initialBusinessInfo);
  const [salons, setSalons] = useState<Salon[]>(initialSalons);
  const [selectedSalonId, setSelectedSalonId] = useState<string | null>(
    initialSelectedSalonId ||
      (initialSalons.length === 1 ? initialSalons[0]?.id : null)
  );
  
  // Sync selectedSalonId when initialSelectedSalonId prop changes
  // This handles the case where the wrapper sets the salon ID after initial render
  useEffect(() => {
    if (initialSelectedSalonId && initialSelectedSalonId !== selectedSalonId) {
      setSelectedSalonId(initialSelectedSalonId);
    }
  }, [initialSelectedSalonId]);

  // Update salons when initialSalons prop changes (for progressive loading of salon details)
  useEffect(() => {
    if (initialSalons.length > 0) {
      setSalons(initialSalons);
    }
  }, [initialSalons]);
  
  // Derive selectedSalon from salons array and selectedSalonId
  const selectedSalon = useMemo(() => {
    if (!selectedSalonId) return null;
    const salon = salons.find((s) => s.id === selectedSalonId) || null;
    return salon;
  }, [salons, selectedSalonId]);

  // Services State - use initialServices directly since it updates from props
  const [services, setServices] = useState<Service[]>(initialServices);
  const [popularServices, setPopularServices] = useState<Service[]>(
    initialPopularServices
  );
  const [bundles, setBundles] = useState<Bundle[]>(initialBundles);
  const [selectedBundles, setSelectedBundles] = useState<Bundle[]>([]);
  const [locationType, setLocationType] = useState<string | null>(null);
  const [formattedServices, setFormattedServices] = useState<GroupedServices[]>(
    []
  );
  const [selectedServices, setSelectedServices] = useState<BookingService[]>(
    []
  );
  // Group booking: attendees (Me = position 1) and which guest is currently shopping
  const meGuestRef = useRef<BookingGuest | null>(null);
  const [guests, setGuests] = useState<BookingGuest[]>([]);
  const [activeGuestId, setActiveGuestId] = useState<string | null>(null);
  // Use external loading state passed from BookingPageWrapper
  const loadingServices = externalServicesLoading;

  // Packages State - use initialPackages directly since it updates from props
  const [packages, setPackages] = useState<Package[]>(initialPackages);

  // Update bundles when initialBundles prop changes (for progressive loading)
  useEffect(() => {
    setBundles(initialBundles || []);
  }, [initialBundles]);
  
  // Update services when initialServices prop changes (for progressive loading)
  useEffect(() => {
    if (initialServices.length > 0) {
      setServices(initialServices);
    }
  }, [initialServices]);

  useEffect(() => {
    setPopularServices(initialPopularServices);
  }, [initialPopularServices]);

  // Update packages when initialPackages prop changes (for progressive loading)
  useEffect(() => {
    if (initialPackages.length > 0) {
      setPackages(initialPackages);
    }
  }, [initialPackages]);
  const [formattedPackages, setFormattedPackages] = useState<BookingPackage[]>(
    []
  );
  const [selectedPackages, setSelectedPackages] = useState<BookingPackage[]>(
    []
  );
  const [giftCardCart, setGiftCardCartState] = useState<GiftCardCartItem | null>(
    null
  );
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const [selectedProducts, setSelectedProducts] = useState<BookingProduct[]>(
    []
  );
  const [productOrder, setProductOrder] = useState<ProductOrderSummary | null>(
    null
  );

  useEffect(() => {
    setProducts(initialProducts || []);
  }, [initialProducts]);

  // Staff State
  const [selectedStaff, setSelectedStaff] = useState<SalonStaff | null>(null);
  const [staffOption, setStaffOption] = useState<string | null>(null);
  const [availableServiceStaffs, setAvailableServiceStaffs] = useState<
    SalonStaff[]
  >([]);

  // Client State
  const [serviceClient, setServiceClient] = useState<Client | null>(null);

  // Appointment State
  const [appointmentDateTime, setAppointmentDateTime] = useState<string | null>(
    null
  );
  const [priceSummary, setPriceSummary] = useState<AppointmentPreview | null>(
    null
  );
  const [packagesPriceSummary, setPackagesPriceSummary] = useState<any | null>(
    null
  );
  const [bookedAppointment, setBookedAppointment] =
    useState<Appointment | null>(null);
  const [existingAppointment, setExistingAppointment] =
    useState<Appointment | null>(null);
  const [appointmentPaymentType, setAppointmentPaymentType] = useState<
    string | null
  >(null);
  const [actualAmountPaid, setActualAmountPaid] = useState<number | null>(null);
  const [actualProcessingFee, setActualProcessingFee] = useState<number | null>(null);

  // Voucher State
  const [voucherToBeRedeemed, setVoucherToBeRedeemed] =
    useState<PackageVoucher | null>(null);

  const [openVoucherRedemptionModal, setOpenVoucherRedemptionModal] =
    useState(false);

  // Selected time slot state (for availability polling - double-booking prevention)
  const [selectedDate, setSelectedDate] = useState<string | null>(null); // "YYYY-MM-DD"
  const [selectedTime, setSelectedTime] = useState<string | null>(null); // "HH:MM"

  // Slot reservation timer state (5-minute countdown from slot selection)
  const [slotSelectionTime, setSlotSelectionTime] = useState<number | null>(null);

  // Auto-trigger next-available search after slot/resource race on confirm
  const [shouldFindNextAvailable, setShouldFindNextAvailable] = useState(false);

  // Promo/Rewards State
  const [promoCodeApplication, setPromoCodeApplication] = useState<any | null>(
    null
  );
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [rewards, setRewards] = useState<ClientMilestone[]>([]);
  const [selectedReward, setSelectedReward] = useState<ClientMilestone | null>(
    null
  );
  const [rewardSelected, setRewardSelected] = useState(false);

  // UI State
  const [activeTab, setActiveTab] = useState(BookingTabName.SERVICES);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const [searchWrapperIsOpen, setSearchWrapperIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Workflow Form State
  const [workflowForm, setWorkflowForm] = useState<WorkflowForm | null>(null);
  const [workflowFormAnswers, setWorkflowFormAnswers] = useState<WorkflowFormAnswers>({});
  const [showWorkflowFormModal, setShowWorkflowFormModal] = useState(false);
  const [workflowFormSubmissionId, setWorkflowFormSubmissionId] = useState<string | null>(null);

  // Refs
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const bookedAppointmentRef = useRef<Appointment | null>(null);
  const selectedSalonIdRef = useRef<string | null>(selectedSalonId);

  // Keep refs in sync with state
  useEffect(() => {
    bookedAppointmentRef.current = bookedAppointment;
  }, [bookedAppointment]);

  useEffect(() => {
    selectedSalonIdRef.current = selectedSalonId;
  }, [selectedSalonId]);

  // Toast function using Sonner
  const addToast = useCallback(
    (toastData: {
      message: string;
      variant: "success" | "error" | "warning";
    }) => {
      switch (toastData.variant) {
        case "success":
          toast.success(toastData.message);
          break;
        case "error":
          toast.error(toastData.message);
          break;
        case "warning":
          toast.warning(toastData.message);
          break;
        default:
          toast(toastData.message);
      }
    },
    []
  );

  const setGiftCardCart = useCallback(
    (giftCard: GiftCardCartItem | null) => {
      if (
        giftCard &&
        (selectedServices.length > 0 ||
          selectedPackages.length > 0 ||
          selectedProducts.length > 0)
      ) {
        addToast({
          message: "Please clear your cart before adding a gift card.",
          variant: "error",
        });
        return;
      }
      setGiftCardCartState(giftCard);
    },
    [
      addToast,
      selectedPackages.length,
      selectedServices.length,
      selectedProducts.length,
    ]
  );

  const clearGiftCardCart = useCallback(() => {
    setGiftCardCartState(null);
  }, []);

  const updateGiftCardCartQuantity = useCallback((quantity: number) => {
    setGiftCardCartState((prev) => {
      if (!prev) return prev;
      const safeQuantity = Math.max(1, Math.floor(quantity || 1));
      return { ...prev, quantity: safeQuantity };
    });
  }, []);

  const clearProductCart = useCallback(() => {
    setSelectedProducts([]);
  }, []);

  const modifyProductInCart = useCallback(
    (productId: string, action: "add" | "remove" | "clear") => {
      if (action === "clear") {
        setSelectedProducts([]);
        return;
      }

      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding products",
          variant: "error",
        });
        return;
      }

      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear vouchers from cart before adding products",
          variant: "error",
        });
        return;
      }

      setSelectedProducts((prev) => {
        const existing = prev.find((product) => product.id === productId);
        const catalogProduct = products.find((product) => product.id === productId);
        const stockCount = catalogProduct?.stockCount ?? existing?.stockCount ?? 0;

        if (action === "add") {
          if ((stockCount ?? 0) < 1) {
            addToast({
              message: "This product is out of stock",
              variant: "error",
            });
            return prev;
          }
          if (existing) {
            if (existing.quantity >= stockCount) {
              addToast({
                message: "No more stock available for this product",
                variant: "error",
              });
              return prev;
            }
            return prev.map((product) =>
              product.id === productId
                ? { ...product, quantity: product.quantity + 1 }
                : product
            );
          }
          if (!catalogProduct) return prev;
          return [
            ...prev,
            { ...catalogProduct, quantity: 1, selected: true },
          ];
        }

        if (action === "remove" && existing) {
          if (existing.quantity > 1) {
            return prev.map((product) =>
              product.id === productId
                ? { ...product, quantity: product.quantity - 1 }
                : product
            );
          }
          return prev.filter((product) => product.id !== productId);
        }

        return prev;
      });
    },
    [
      addToast,
      giftCardCart,
      products,
      selectedPackages.length,
    ]
  );

  const updateProductCartQuantity = useCallback(
    (productId: string, quantity: number) => {
      const catalogProduct = products.find((product) => product.id === productId);
      const maxStock = catalogProduct?.stockCount ?? 0;
      const safeQuantity = Math.max(1, Math.floor(quantity || 1));
      if (maxStock < 1) {
        addToast({
          message: "This product is out of stock",
          variant: "error",
        });
        return;
      }
      if (safeQuantity > maxStock) {
        addToast({
          message: "No more stock available for this product",
          variant: "error",
        });
        return;
      }
      setSelectedProducts((prev) =>
        prev.map((product) =>
          product.id === productId
            ? { ...product, quantity: safeQuantity }
            : product
        )
      );
    },
    [addToast, products]
  );

  // Check if mobile on mount
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Format services into groups - re-run when services change
  useEffect(() => {
    if (services.length > 0) {
      const grouped = formatServicesIntoGroups(services);
      setFormattedServices(grouped);
    }
  }, [services]);

  // Format vouchers (only packages that are purchasable as vouchers)
  useEffect(() => {
    if (packages.length > 0) {
      const formatted = packages.map((pkg) => ({
        ...pkg,
        quantity: 0,
        selected: false,
        cartQuantity: 0,
      }));

      setFormattedPackages(formatted);
    } else {
      setFormattedPackages([]);
    }
  }, [packages]);

  // Helper function to format services
  const formatServicesIntoGroups = (services: Service[]): GroupedServices[] => {
    const groups: { [key: string]: GroupedServices } = {};

    services.forEach((service) => {
      const categoryName = service.category?.name || "Other";
      const categoryKey = service.category?.id || "other";

      if (!groups[categoryKey]) {
        groups[categoryKey] = {
          name: categoryName,
          category: categoryKey,
          description: service.category?.description ?? null,
          show: true,
          services: [],
        };
      }
      groups[categoryKey].services.push({
        ...service,
        selected: false,
        quantity: 1,
      });
    });

    return Object.values(groups).sort((a, b) => {
      // Sort by orderIndex if available, otherwise alphabetically
      const aOrder =
        services.find((s) => s.category?.id === a.category)?.category
          ?.orderIndex ?? 999;
      const bOrder =
        services.find((s) => s.category?.id === b.category)?.category
          ?.orderIndex ?? 999;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });
  };

  // Navigation

  // Delete a held appointment to release the reserved time slot
  const deleteHeldAppointment = useCallback(
    async (appointment: Appointment, salonId: string) => {
      try {
        await graphqlFetch(DELETE_APPOINTMENT, {
          salonId,
          id: appointment.id,
        });
      } catch (error) {
        // Fire and forget - don't block navigation if deletion fails.
        // The slot will be released automatically when reserved_until expires.
        console.error("Failed to delete held appointment:", error);
      }
      // Clear the localStorage backup record (mirrors Payment.tsx's HELD_APPOINTMENT_KEY)
      try {
        localStorage.removeItem("splice_held_appointment");
      } catch {
        // Ignore — localStorage may be unavailable
      }
      setBookedAppointment(null);
    },
    []
  );

  const handleNextStep = useCallback((page?: number) => {
    // If navigating away from the Payment step while an appointment has been
    // created (but payment hasn't completed), delete the appointment to
    // release the held time slot immediately.
    setStep((prev) => {
      const targetStep = page !== undefined ? page : prev + 1;
      if (prev === BookingStep.PAYMENT && targetStep !== BookingStep.SUCCESS) {
        if (bookedAppointmentRef.current && selectedSalonIdRef.current) {
          deleteHeldAppointment(
            bookedAppointmentRef.current,
            selectedSalonIdRef.current
          );
        }
      }
      return targetStep;
    });
    scrollToTop();
  }, [deleteHeldAppointment]);

  const scrollToTop = useCallback(() => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: 0,
        behavior: "instant",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "instant",
      });
    }
  }, []);

  // Service Management
  const showOrHideCategory = useCallback(
    (categoryName: string, show?: boolean) => {
      setFormattedServices((prev) =>
        prev.map((category) =>
          category.name === categoryName
            ? { ...category, show: show !== undefined ? show : !category.show }
            : category
        )
      );
    },
    []
  );

  const guestLabel = useCallback((guest: BookingGuest) => {
    if (guest.name?.trim()) return guest.name.trim();
    return guest.position === 1 ? "Me" : `Guest ${guest.position}`;
  }, []);

  const ensureMeGuest = useCallback((): BookingGuest => {
    if (meGuestRef.current) {
      const me = meGuestRef.current;
      setGuests((prev) =>
        prev.some((g) => g.id === me.id)
          ? prev
          : [me, ...prev.filter((g) => g.position !== 1)]
      );
      setSelectedServices((prev) =>
        prev.map((s) => (s.guestId ? s : { ...s, guestId: me.id }))
      );
      return me;
    }

    const me: BookingGuest = { id: crypto.randomUUID(), position: 1 };
    meGuestRef.current = me;
    setGuests((prev) => [me, ...prev.filter((g) => g.position !== 1)]);
    setSelectedServices((prev) =>
      prev.map((s) => (s.guestId ? s : { ...s, guestId: me.id }))
    );
    return me;
  }, []);

  const addGuest = useCallback(
    (name?: string): BookingGuest | null => {
      ensureMeGuest();
      const additionalCount = guests.filter((g) => g.position > 1).length;
      if (additionalCount >= MAX_ADDITIONAL_GUESTS) {
        addToast({
          message: `You can add up to ${MAX_ADDITIONAL_GUESTS} guests`,
          variant: "error",
        });
        return null;
      }

      const nextPosition =
        Math.max(1, ...guests.map((g) => g.position), 1) + 1;
      const guest: BookingGuest = {
        id: crypto.randomUUID(),
        position: nextPosition,
        ...(name?.trim() ? { name: name.trim() } : {}),
      };
      setGuests((prev) =>
        [...prev, guest].sort((a, b) => a.position - b.position)
      );
      setActiveGuestId(guest.id);
      return guest;
    },
    [guests, ensureMeGuest, addToast]
  );

  const removeGuest = useCallback(
    (guestId: string) => {
      const guest = guests.find((g) => g.id === guestId);
      if (!guest || guest.position === 1) {
        addToast({
          message: "You cannot remove yourself from the booking",
          variant: "error",
        });
        return;
      }

      setGuests((prev) => prev.filter((g) => g.id !== guestId));
      setSelectedServices((prev) => prev.filter((s) => s.guestId !== guestId));
      setActiveGuestId((current) => (current === guestId ? null : current));
    },
    [guests, addToast]
  );

  const renameGuest = useCallback((guestId: string, name: string) => {
    setGuests((prev) =>
      prev.map((g) =>
        g.id === guestId ? { ...g, name: name.trim() || undefined } : g
      )
    );
  }, []);

  const selectService = useCallback(
    (serviceId: string) => {
      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding services",
          variant: "error",
        });
        return;
      }
      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before adding services",
          variant: "error",
        });
        return;
      }

      const targetGuestId = activeGuestId ?? undefined;

      setSelectedServices((prev) => {
        const exists = prev.find((s) =>
          matchesServiceGuest(s, serviceId, targetGuestId)
        );

        if (exists) {
          return prev.filter(
            (s) => !matchesServiceGuest(s, serviceId, targetGuestId)
          );
        }

        const service = services.find((s) => s.id === serviceId);
        if (!service) return prev;

        return [
          ...prev,
          {
            ...service,
            selected: true,
            quantity: 1,
            cartQuantity: 1,
            ...(targetGuestId ? { guestId: targetGuestId } : {}),
          },
        ];
      });

      setFormattedServices((prev) =>
        prev.map((category) => ({
          ...category,
          services: category.services.map((service) => {
            if (service.id !== serviceId) return service;
            const willBeSelected = !selectedServices.some((s) =>
              matchesServiceGuest(s, serviceId, targetGuestId)
            );
            const stillSelectedByOthers = selectedServices.some(
              (s) =>
                s.id === serviceId &&
                !matchesServiceGuest(s, serviceId, targetGuestId)
            );
            return {
              ...service,
              selected: willBeSelected || stillSelectedByOthers,
            };
          }),
        }))
      );
    },
    [
      giftCardCart,
      selectedPackages,
      services,
      addToast,
      activeGuestId,
      selectedServices,
    ]
  );

  const selectServiceWithVariant = useCallback(
    (serviceId: string, variant?: ServiceVariant | null) => {
      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding services",
          variant: "error",
        });
        return;
      }
      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before adding services",
          variant: "error",
        });
        return;
      }

      const targetGuestId = activeGuestId ?? undefined;

      setSelectedServices((prev) => {
        const exists = prev.find((s) =>
          matchesServiceGuest(s, serviceId, targetGuestId)
        );

        if (exists) {
          return prev.filter(
            (s) => !matchesServiceGuest(s, serviceId, targetGuestId)
          );
        }

        let originalServiceId = serviceId;
        if (serviceId.includes("-") && variant) {
          const variantId = variant.id;
          if (serviceId.endsWith(`-${variantId}`)) {
            originalServiceId = serviceId.slice(0, -(variantId.length + 1));
          }
        }

        const service = services.find((s) => s.id === originalServiceId);
        if (!service) return prev;

        const serviceWithVariant = {
          ...service,
          id: serviceId,
          selected: true,
          quantity: 1,
          cartQuantity: 1,
          ...(targetGuestId ? { guestId: targetGuestId } : {}),
          ...(variant && {
            variantId: variant.id,
            variantName: variant.name,
            variantPrice: variant.price,
            variantDuration: variant.duration,
            price: variant.price,
            duration: variant.duration,
            name: variant.name,
            description: variant.description || service.description,
          }),
        };

        return [...prev, serviceWithVariant];
      });

      setFormattedServices((prev) =>
        prev.map((category) => ({
          ...category,
          services: category.services.map((service) => {
            if (service.id !== serviceId) return service;
            const willBeSelected = !selectedServices.some((s) =>
              matchesServiceGuest(s, serviceId, targetGuestId)
            );
            const stillSelectedByOthers = selectedServices.some(
              (s) =>
                s.id === serviceId &&
                !matchesServiceGuest(s, serviceId, targetGuestId)
            );
            return {
              ...service,
              selected: willBeSelected || stillSelectedByOthers,
            };
          }),
        }))
      );
    },
    [
      giftCardCart,
      selectedPackages,
      services,
      addToast,
      activeGuestId,
      selectedServices,
    ]
  );

  const roundCurrency = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

  const getBundleCustomerTotal = useCallback((bundle: Bundle) => {
    if (bundle.usesCustomPrice && typeof bundle.customPrice === "number") {
      return roundCurrency(bundle.customPrice);
    }
    if (typeof bundle.discountPercentage === "number" && bundle.discountPercentage > 0) {
      return roundCurrency(bundle.totalPrice * (1 - bundle.discountPercentage / 100));
    }
    return roundCurrency(bundle.totalPrice);
  }, []);

  const getBundleScheduleType = useCallback((bundle: Bundle): BundleScheduleType => {
    const raw = String(bundle.scheduleType || "sequence").toLowerCase();
    return raw === "parallel" ? "parallel" : "sequence";
  }, []);

  const selectBundle = useCallback(
    (bundleId: string) => {
      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding services",
          variant: "error",
        });
        return;
      }
      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before adding services",
          variant: "error",
        });
        return;
      }

      const bundle = bundles.find((b) => b.id === bundleId);
      if (!bundle) return;

      const alreadySelected = selectedBundles.some((b) => b.id === bundleId);
      if (alreadySelected) {
        setSelectedBundles((prev) => prev.filter((b) => b.id !== bundleId));
        setSelectedServices((prev) => prev.filter((s) => s.bundleId !== bundleId));
        return;
      }

      const scheduleType = getBundleScheduleType(bundle);
      const bundleServices = Array.isArray(bundle.bundleServices)
        ? [...bundle.bundleServices].sort(
            (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
          )
        : [];

      const expanded = bundleServices
        .map((bs) => {
          // Prefer the full `services` list entry (has pricingType, variants, etc),
          // and fall back to the minimal bundleServices.service payload.
          const resolvedService =
            services.find((s) => s.id === bs?.serviceId || s.id === bs?.service?.id) ||
            bs?.service;

          if (!resolvedService) return null;
          const quantity = bs.quantity || 1;
          const baseUnit = resolvedService.price || 0;
          return {
            ...resolvedService,
            id: resolvedService.id,
            price: baseUnit,
            originalPrice: baseUnit,
            quantity,
            selected: true,
            bundleId: bundle.id,
            bundleScheduleType: scheduleType,
          } satisfies BookingService;
        })
        .filter(Boolean) as BookingService[];

      const baseTotal = expanded.reduce(
        (sum, s) => sum + (s.originalPrice || s.price || 0) * (s.quantity || 1),
        0
      );
      const customerTotal = getBundleCustomerTotal(bundle);

      let remaining = customerTotal;
      const allocated = expanded.map((service, index) => {
        if (baseTotal <= 0) return service;
        const lineBase = (service.originalPrice || service.price || 0) * (service.quantity || 1);
        const isLast = index === expanded.length - 1;
        const lineAllocated = isLast
          ? remaining
          : roundCurrency((lineBase / baseTotal) * customerTotal);
        remaining = roundCurrency(remaining - lineAllocated);
        const unitAllocated = roundCurrency(lineAllocated / (service.quantity || 1));
        return { ...service, price: unitAllocated };
      });

      // Add bundle services, keep any non-bundle services and other bundle services.
      setSelectedBundles((prev) => [...prev, bundle]);
      setSelectedServices((prev) => [...prev, ...allocated]);
    },
    [
      giftCardCart,
      selectedPackages.length,
      bundles,
      selectedBundles,
      addToast,
      getBundleCustomerTotal,
      getBundleScheduleType,
      services,
    ]
  );

  const clearSelectedBundles = useCallback(() => {
    setSelectedBundles([]);
    setSelectedServices((prev) => prev.filter((s) => !s.bundleId && !s.bundleScheduleType));
  }, []);

  const deleteService = useCallback(
    (serviceId: string, guestId?: string) => {
      const targetGuestId = guestId ?? activeGuestId ?? undefined;
      const selectedService = selectedServices.find((service) =>
        matchesServiceGuest(service, serviceId, targetGuestId)
      );
      if (selectedService?.bundleId) return;

      setSelectedServices((prev) =>
        prev.filter(
          (s) => !matchesServiceGuest(s, serviceId, targetGuestId)
        )
      );
      setFormattedServices((prev) =>
        prev.map((category) => ({
          ...category,
          services: category.services.map((service) => {
            if (service.id !== serviceId) return service;
            const stillSelected = selectedServices.some(
              (s) =>
                s.id === serviceId &&
                !matchesServiceGuest(s, serviceId, targetGuestId)
            );
            return { ...service, selected: stillSelected };
          }),
        }))
      );
    },
    [selectedServices, activeGuestId]
  );

  const clearServicesInCart = useCallback(() => {
    setSelectedServices([]);
    setFormattedServices((prev) =>
      prev.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          selected: false,
        })),
      }))
    );
  }, []);

  // Package Management
  const modifyPackageInCart = useCallback(
    (packageId: string, action: "add" | "remove" | "clear") => {
      if (action === "clear") {
        setSelectedPackages([]);
        return;
      }

      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before adding packages",
          variant: "error",
        });
        return;
      }

      if (selectedServices.length > 0) {
        addToast({
          message: "Please clear services from cart before adding packages",
          variant: "error",
        });
        return;
      }

      if (selectedProducts.length > 0) {
        addToast({
          message: "Please clear products from cart before adding packages",
          variant: "error",
        });
        return;
      }

      setSelectedPackages((prev) => {
        const existingPackage = prev.find((p) => p.id === packageId);

        if (action === "add") {
          if (existingPackage) {
            return prev.map((p) =>
              p.id === packageId ? { ...p, quantity: p.quantity + 1 } : p
            );
          } else {
            const pkg = formattedPackages.find((p) => p.id === packageId);
            if (pkg) {
              return [...prev, { ...pkg, quantity: 1, selected: true }];
            }
          }
        } else if (action === "remove") {
          if (existingPackage) {
            if (existingPackage.quantity > 1) {
              return prev.map((p) =>
                p.id === packageId ? { ...p, quantity: p.quantity - 1 } : p
              );
            } else {
              return prev.filter((p) => p.id !== packageId);
            }
          }
        }

        return prev;
      });
    },
    [giftCardCart, selectedServices, selectedProducts, formattedPackages, addToast]
  );

  const modifyServiceInCart = useCallback(
    (serviceId: string, action: "add" | "remove" | "clear", guestId?: string) => {
      if (action === "clear") {
        clearServicesInCart();
        return;
      }

      if (giftCardCart) {
        addToast({
          message: "Please clear gift cards from cart before modifying services",
          variant: "error",
        });
        return;
      }

      if (selectedPackages.length > 0) {
        addToast({
          message: "Please clear packages from cart before modifying services",
          variant: "error",
        });
        return;
      }

      const targetGuestId = guestId ?? activeGuestId ?? undefined;

      setSelectedServices((prev) => {
        let working = prev;
        let existingService = working.find(
          (s) => matchesServiceGuest(s, serviceId, targetGuestId) && !s.bundleId
        );

        const bundleLine = working.find(
          (s) => matchesServiceGuest(s, serviceId, targetGuestId) && s.bundleId
        );
        if (bundleLine?.bundleId) {
          const bundleId = bundleLine.bundleId;
          setSelectedBundles((prevBundles) =>
            prevBundles.filter((b) => b.id !== bundleId)
          );

          working = working
            .filter((s) => s.bundleId !== bundleId)
            .concat(
              working
                .filter((s) => s.bundleId === bundleId)
                .map((s) => ({
                  ...s,
                  bundleId: null,
                  bundleScheduleType: null,
                  price: s.originalPrice ?? s.price,
                  originalPrice: undefined,
                }))
            );

          existingService = working.find(
            (s) =>
              matchesServiceGuest(s, serviceId, targetGuestId) && !s.bundleId
          );
        }

        if (action === "add") {
          if (existingService) {
            return working.map((s) =>
              matchesServiceGuest(s, serviceId, targetGuestId)
                ? { ...s, quantity: (s.quantity || 1) + 1 }
                : s
            );
          }
        } else if (action === "remove") {
          if (existingService) {
            if ((existingService.quantity || 1) > 1) {
              return working.map((s) =>
                matchesServiceGuest(s, serviceId, targetGuestId)
                  ? { ...s, quantity: (s.quantity || 1) - 1 }
                  : s
              );
            }
            return working.filter(
              (s) => !matchesServiceGuest(s, serviceId, targetGuestId)
            );
          }
        }

        return working;
      });
    },
    [
      giftCardCart,
      selectedPackages,
      clearServicesInCart,
      addToast,
      activeGuestId,
    ]
  );

  // Search Management
  const closeSearch = useCallback(() => {
    setDebouncedSearchQuery("");
    setSearchWrapperIsOpen(false);
    setSearchCount(0);
    if (services.length > 0) {
      setFormattedServices(formatServicesIntoGroups(services));
    }
  }, [services]);

  const openSearchWrapper = useCallback((box?: boolean) => {
    setSearchWrapperIsOpen(true);
  }, []);

  const resetBooking = useCallback(() => {
    setStep(BookingStep.INITIAL);
    setSelectedServices([]);
    setSelectedPackages([]);
    setGiftCardCartState(null);
    setSelectedProducts([]);
    setProductOrder(null);
    setVoucherToBeRedeemed(null);
    setSelectedStaff(null);
    setStaffOption(null);
    setGuests([]);
    setActiveGuestId(null);
    meGuestRef.current = null;
    setAppointmentDateTime(null);
    setServiceClient(null);
    setPriceSummary(null);
    setPromoCode(null);
    setPromoCodeApplication(null);
    setBookedAppointment(null);
    setActualAmountPaid(null);
    setActualProcessingFee(null);
    setSelectedReward(null);
    setRewardSelected(false);
    // Reset workflow form state
    setWorkflowForm(null);
    setWorkflowFormAnswers({});
    setShowWorkflowFormModal(false);
    setWorkflowFormSubmissionId(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSlotSelectionTime(null);
    setShouldFindNextAvailable(false);
  }, []);

  // Workflow Form Actions
  const updateWorkflowFormAnswer = useCallback(
    (fieldId: string, value: string | string[] | boolean) => {
      setWorkflowFormAnswers((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
    },
    []
  );

  const clearWorkflowForm = useCallback(() => {
    setWorkflowForm(null);
    setWorkflowFormAnswers({});
    setShowWorkflowFormModal(false);
    setWorkflowFormSubmissionId(null);
  }, []);

  // Clear selected date/time (for availability polling reset)
  const clearSelectedDateTime = useCallback(() => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSlotSelectionTime(null);
  }, []);

  const value: BookingContextType = {
    // State
    step,
    services,
    popularServices,
    bundles,
    selectedBundles,
    formattedServices,
    locationType,
    formattedPackages,
    selectedServices,
    selectedPackages,
    products,
    selectedProducts,
    giftCardCart,
    productOrder,
    guests,
    activeGuestId,
    selectedStaff,
    availableServiceStaffs,
    businessInfo,
    salons,
    selectedSalon,
    selectedSalonId,
    appointmentDateTime,
    priceSummary,
    packagesPriceSummary,
    serviceClient,
    voucherToBeRedeemed,
    promoCodeApplication,
    promoCode,
    rewards,
    selectedReward,
    activeTab,
    openVoucherRedemptionModal,
    bookedAppointment,
    appointmentPaymentType,
    actualAmountPaid,
    actualProcessingFee,
    staffOption,
    debouncedSearchQuery,
    searchCount,
    searchWrapperIsOpen,
    rewardSelected,
    faqs,
    reviews,
    isMobile,
    existingAppointment,
    loadingServices,
    promos,
    // Workflow Form State
    workflowForm,
    workflowFormAnswers,
    showWorkflowFormModal,
    workflowFormSubmissionId,
    selectedDate,
    selectedTime,
    slotSelectionTime,
    shouldFindNextAvailable,

    // Actions
    handleNextStep,
    setLocationType,
    setFormattedServices,
    showOrHideCategory,
    selectService,
    selectServiceWithVariant,
    selectBundle,
    clearSelectedBundles,
    setSelectedServices,
    addGuest,
    removeGuest,
    renameGuest,
    setActiveGuestId,
    ensureMeGuest,
    guestLabel,
    setSelectedSalonId,
    setAppointmentDateTime,
    setServiceClient,
    setActiveTab,
    setPriceSummary,
    clearServicesInCart,
    setVoucherToBeRedeemed,
    setOpenVoucherRedemptionModal,
    setBookedAppointment,
    setPromoCodeApplication,
    setPromoCode,
    setPackagesPriceSummary,
    setAppointmentPaymentType,
    setActualAmountPaid,
    setActualProcessingFee,

    setStaffOption,
    setRewards,
    setSelectedReward,
    closeSearch,
    openSearchWrapper,
    setDebouncedSearchQuery,
    setRewardSelected,
    deleteService,
    setSelectedStaff,
    setAvailableServiceStaffs,
    modifyPackageInCart,
    modifyServiceInCart,
    setGiftCardCart,
    updateGiftCardCartQuantity,
    clearGiftCardCart,
    modifyProductInCart,
    updateProductCartQuantity,
    clearProductCart,
    setProductOrder,
    addToast,
    scrollToTop,
    resetBooking,
    // Workflow Form Actions
    setWorkflowForm,
    setWorkflowFormAnswers,
    setShowWorkflowFormModal,
    setWorkflowFormSubmissionId,
    updateWorkflowFormAnswer,
    clearWorkflowForm,
    setSelectedDate,
    setSelectedTime,
    clearSelectedDateTime,
    setShouldFindNextAvailable,
    setSlotSelectionTime,
    deleteHeldAppointment,
  };

  return (
    <BookingContext.Provider value={value}>
      <div ref={modalContentRef}>{children}</div>
    </BookingContext.Provider>
  );
}

export default BookingContext;
